import React, { useState, useEffect } from 'react';
import axios from '../axiosConfig';
import { useNavigate } from 'react-router-dom';
import './CheckoutPage.css';
import OrderCompletePopup from '../components/OrderCompletePopup';
import { completeImageUrl } from '../utils/imageUrl';
import { itemsWord } from '../utils/pluralize';

export default function CheckoutPage() {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    delivery_city: '',
    delivery_type: '',
    delivery_department: '',
    delivery_address: '',
  });
  const [, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderComplete, setOrderComplete] = useState(false);
  const [isLoadingUserData, setIsLoadingUserData] = useState(true);
  const [cartItems, setCartItems] = useState([]);
  const navigate = useNavigate();

  // Короткий підсумок замовлення (те, що зараз у кошику)
  useEffect(() => {
    if (!localStorage.getItem('authToken')) return;
    axios
      .get('/api/v1/products?cart=true')
      .then((response) => setCartItems(response.data || []))
      .catch((err) => console.error('Помилка завантаження кошика:', err));
  }, []);

  const cartLines = cartItems.map((product) => {
    const variation = product.chosen_variation ||
      product.product_variations?.find((v) => v.main) ||
      product.product_variations?.[0];
    const quantity = product.chosen_variation?.quantity || 1;
    const price = variation ? parseFloat(variation.price) || 0 : 0;
    return { id: product.id, title: product.title, quantity, price };
  });
  // the shops (sellers) behind the order: one avatar per shop
  const shops = [];
  cartItems.forEach((product) => {
    const key = product.account_id || product.account_name;
    if (!key || shops.some((shop) => shop.key === key)) return;
    // nice shop name from the API ("Tea Room"); fall back to the handle without the @
    const name = product.account_title || (product.account_name || '').replace(/^@/, '');
    shops.push({
      key,
      name,
      image: product.account_url ? completeImageUrl(product.account_url) : null,
    });
  });
  const cartCount = cartLines.reduce((sum, line) => sum + line.quantity, 0);
  const cartTotal = cartLines.reduce((sum, line) => sum + line.price * line.quantity, 0);

  // Завантаження даних поточного користувача для заповнення форми
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      axios
        .get(`${process.env.REACT_APP_API_URL}/api/v1/users/current`)
        .then(response => {
          const userData = response.data;
          setFormData({
            first_name: userData.first_name || '',
            last_name: userData.last_name || '',
            email: userData.email || '',
            phone_number: userData.phone_number || '',
            delivery_city: userData.delivery_city || '',
            delivery_type: userData.delivery_type || 'department',
            delivery_department: userData.delivery_department || '',
            delivery_address: userData.delivery_address || '',
          });
        })
        .catch(err => {
          console.error('Помилка завантаження даних користувача:', err);
          // Set default if API fails
          setFormData(prev => ({
            ...prev,
            delivery_type: 'department'
          }));
        })
        .finally(() => {
          setIsLoadingUserData(false);
        });
    } else {
      // If no token, set default values
      setFormData(prev => ({
        ...prev,
        delivery_type: 'department'
      }));
      setIsLoadingUserData(false);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      // Clear delivery fields when switching delivery type
      if (name === 'delivery_type') {
        newData.delivery_department = '';
        newData.delivery_address = '';
      }
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate all required fields
    if (!formData.first_name.trim()) {
      setError("Будь ласка, введіть ім'я");
      return;
    }
    if (!formData.last_name.trim()) {
      setError("Будь ласка, введіть прізвище");
      return;
    }
    if (!formData.email.trim()) {
      setError("Будь ласка, введіть електронну пошту");
      return;
    }
    if (!formData.phone_number.trim()) {
      setError("Будь ласка, введіть телефон");
      return;
    }
    if (!formData.delivery_city.trim()) {
      setError("Будь ласка, введіть місто");
      return;
    }
    if (!formData.delivery_type) {
      setError("Будь ласка, виберіть тип доставки");
      return;
    }
    if (formData.delivery_type === 'department' && !formData.delivery_department.trim()) {
      setError("Будь ласка, введіть номер відділення");
      return;
    }
    if (formData.delivery_type === 'postomat' && !formData.delivery_department.trim()) {
      setError("Будь ласка, введіть номер поштомату");
      return;
    }
    if (formData.delivery_type === 'address' && !formData.delivery_address.trim()) {
      setError("Будь ласка, введіть адресу");
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        order: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone_number: formData.phone_number,
          delivery_city: formData.delivery_city,
          delivery_type: formData.delivery_type,
          delivery_department: formData.delivery_department,
          delivery_address: formData.delivery_address,
          // Додайте інші деталі замовлення за потребою.
        }
      };
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/v1/orders`, payload);
      console.log('Замовлення оформлено:', response.data);
      
      // User data is automatically saved in the backend during order creation
      // but we can also explicitly update it to ensure persistence
      try {
        await axios.patch(`${process.env.REACT_APP_API_URL}/api/v1/users/current`, {
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone_number: formData.phone_number,
          delivery_city: formData.delivery_city,
          delivery_type: formData.delivery_type,
          delivery_department: formData.delivery_department,
          delivery_address: formData.delivery_address,
        });
      } catch (updateErr) {
        console.error('Помилка збереження даних користувача:', updateErr);
        // Don't block the order success if user update fails
      }
      
      setOrderComplete(true);
      // Auto-redirect after 3 seconds, or user can close manually
      const redirectTimer = setTimeout(() => {
        navigate('/orders');
      }, 3000);
      
      // Store timer so it can be cleared if user closes manually
      window.orderCompleteTimer = redirectTimer;
    } catch (err) {
      console.error('Помилка оформлення замовлення:', err);
      const errorMessage = err.response?.data?.errors 
        ? (Array.isArray(err.response.data.errors) 
            ? err.response.data.errors.join(', ') 
            : err.response.data.errors)
        : (err.response?.data?.error || 'Помилка оформлення замовлення. Будь ласка, спробуйте ще раз.');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const field = (id, label, props = {}) => (
    <div className="checkout__field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        name={id}
        value={formData[id]}
        onChange={handleChange}
        required
        {...props}
      />
    </div>
  );

  return (
    <div className="checkout__page">
      <form className="checkout__form" onSubmit={handleSubmit}>
        <h1 className="checkout__title">Оформлення замовлення</h1>

        {cartLines.length > 0 && (
          <div className="checkout__summary">
            {shops.length > 0 && (
              <div className="checkout__summary-shops">
                {shops.slice(0, 4).map((shop) => (
                  <span
                    key={shop.key}
                    className="checkout__summary-shop"
                    title={shop.name}
                    style={shop.image ? { backgroundImage: `url(${shop.image})` } : undefined}
                  >
                    {!shop.image && <span className="checkout__summary-shop-name">{shop.name || 'Магазин'}</span>}
                  </span>
                ))}
              </div>
            )}
            <div className="checkout__summary-text">
              <span className="checkout__summary-label">Ваше замовлення</span>
              <span className="checkout__summary-value">
                {cartCount} {itemsWord(cartCount)} · {cartTotal % 1 === 0 ? cartTotal.toString() : cartTotal.toFixed(2)} ₴
              </span>
            </div>
          </div>
        )}

        <section className="checkout__section">
          <h2 className="checkout__section-title">Контакти</h2>
          <div className="checkout__row">
            {field('first_name', "Ім'я", { type: 'text', autoComplete: 'given-name' })}
            {field('last_name', 'Прізвище', { type: 'text', autoComplete: 'family-name' })}
          </div>
          {field('email', 'Електронна пошта', { type: 'email', autoComplete: 'email', inputMode: 'email' })}
          {field('phone_number', 'Телефон', { type: 'tel', autoComplete: 'tel', inputMode: 'tel' })}
        </section>

        <section className="checkout__section">
          <h2 className="checkout__section-title">Доставка Новою Поштою</h2>
          {field('delivery_city', 'Місто', { type: 'text', autoComplete: 'address-level2' })}

          {!isLoadingUserData && (
            <div className="checkout__segments" role="radiogroup" aria-label="Спосіб доставки">
              {[
                ['department', 'Відділення'],
                ['postomat', 'Поштомат'],
                ['address', 'Адреса'],
              ].map(([value, label]) => (
                <label key={value} className="checkout__segment">
                  <input
                    type="radio"
                    name="delivery_type"
                    value={value}
                    checked={formData.delivery_type === value}
                    onChange={handleChange}
                    required
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          )}

          {formData.delivery_type === 'department' && field('delivery_department', 'Номер відділення', { type: 'text', placeholder: 'Наприклад, 12', inputMode: 'numeric' })}
          {formData.delivery_type === 'postomat' && field('delivery_department', 'Номер поштомату', { type: 'text', placeholder: 'Наприклад, 4521', inputMode: 'numeric' })}
          {formData.delivery_type === 'address' && field('delivery_address', 'Адреса', { type: 'text', placeholder: 'Вулиця, будинок, квартира', autoComplete: 'street-address' })}
        </section>

        {error && <p className="checkout__error" role="alert">{error}</p>}
        <p className="checkout__hint">Натискаючи «Підтвердити», ви оформлюєте замовлення.</p>
      </form>
      {orderComplete && (
        <OrderCompletePopup 
          onClose={() => {
            // Clear auto-redirect timer and navigate immediately
            if (window.orderCompleteTimer) {
              clearTimeout(window.orderCompleteTimer);
            }
            navigate('/orders');
          }} 
        />
      )}
    </div>
  );
}
